import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { v4 as uuid } from 'uuid';
import { isSubCreateMode } from '../../utils/store/store-utils';
import {
  BACKGROUND_CREATE_MODE,
  CONSULT_MODE,
  CREATE_MODE,
  NONE_BACKGROUND_MODE,
  NONE_MODE,
  NONE_SUB_MODE,
  SUB_CONSULT_MODE,
  SUB_CREATE_MODE,
  SUB_UPDATE_MODE,
  UPDATE_MODE,
} from '../mode';
import {
  ALTERNATIVE_LOADING_STATUS,
  FAILURE_STATUS,
  IDLE_STATUS,
  LOADING_STATUS,
  PREPARING_ACTION_STATUS,
  SUCCESS_STATUS,
} from '../status';
import {
  activateById,
  fetchAll, fetchById, inactivateById,
} from './modeloFollowUpAPI';
import i18n from '../../i18n';

const initialState = {
  status: IDLE_STATUS,
  mode: {
    main: NONE_MODE,
    background: NONE_BACKGROUND_MODE,
    sub: NONE_SUB_MODE,
  },
  error: null,
  response: {
    status: null,
    message: null,
    data: null,
  },
  list: [],
  model: {
    description: null,
    sigla: null,
    diasConclusao: null,
    etapaConclusao: {
      id: null,
    },
    tipo: null,
    analiseDesempenho: null,
    etapas: [],
  },
  related: {
    model: {
      etapaModeloFollowUp: {
        id: null,
        description: null,
        tipoPrazoConclusao: null,
        prazoConclusao: null,
        diasUteis: null,
        etapaAtual: {
          id: null,
        },
        proximoEtapa: {
          id: null,
        },
        etapaAnterior: {
          id: null,
        },
      },
    },
  },
};

const fetchAllAsync = createAsyncThunk(
  'modeloFollowUp/fetchAll',
  async () => {
    const { data } = await fetchAll();
    return { data };
  }
);

const fetchByIdAsync = createAsyncThunk(
  'modeloFollowUp/fetchById',
  async (data) => {
    const response = await fetchById(data);
    return {
      response: {
        status: response.status,
        data: response.data,
      },
    };
  }
);

const activateByIdAsync = createAsyncThunk(
  'modeloFollowUp/activateById',
  async (id) => {
    const { status, data } = await activateById(id);
    return { response: { status, data } };
  }
);

const inactivateByIdAsync = createAsyncThunk(
  'modeloFollowUp/inactivateById',
  async (id) => {
    const { status, data } = await inactivateById(id);
    return { response: { status, data } };
  }
);

const modeloFollowUpSlice = createSlice({
  name: 'modeloFollowUp',
  initialState,
  reducers: {
    changeStatusTo: (state, action) => {
      state.status = action.payload.status;
    },
    loading: (state) => {
      state.status = LOADING_STATUS;
    },
    success: (state) => {
      state.status = SUCCESS_STATUS;
    },
    failure: (state) => {
      state.status = FAILURE_STATUS;
    },
    preparingAction: (state) => {
      state.status = PREPARING_ACTION_STATUS;
    },
    resetStatus: (state) => {
      state.status = IDLE_STATUS;
    },
    changeToCreateMode: (state) => {
      state.mode.main = CREATE_MODE;
    },
    changeToConsultMode: (state) => {
      state.mode.main = CONSULT_MODE;
    },
    changeToUpdateMode: (state) => {
      state.mode.main = UPDATE_MODE;
    },
    resetMode: (state) => {
      state.mode.main = NONE_MODE;
    },
    changeToBackgroundCreateMode: (state) => {
      state.mode.background = BACKGROUND_CREATE_MODE;
    },
    resetBackgroundMode: (state) => {
      state.mode.background = NONE_BACKGROUND_MODE;
    },
    changeToSubCreateMode: (state) => {
      state.mode.sub = SUB_CREATE_MODE;
    },
    changeToSubConsultMode: (state) => {
      state.mode.sub = SUB_CONSULT_MODE;
    },
    changeToSubUpdateMode: (state) => {
      state.mode.sub = SUB_UPDATE_MODE;
    },
    resetSubMode: (state) => {
      state.mode.sub = NONE_SUB_MODE;
    },
    setModel: (state, action) => {
      const updatedEtapasList = action.payload.etapas.map(
        (current) => ((current?.id === null)
          ? {
            ...current,
            id: uuid(),
          }
          : current
        )
      );

      state.model = {
        ...action.payload,
        etapas: updatedEtapasList,
      };
    },
    resetModel: (state) => {
      state.model = initialState.model;
    },
    setRelatedEtapaModeloFollowUpModel: (state, action) => {
      state.related.model.etapaModeloFollowUp = {
        ...action.payload,
      };
    },
    addEtapaModeloFollowUp: (state, action) => {
      state.model.etapas = [
        ...state.model.etapas,
        {
          ...action.payload.data,
          id: uuid(),
        }
      ];
      state.related.model.etapaModeloFollowUp = {};
      state.mode.sub = NONE_SUB_MODE;
    },
    updateEtapaModeloFollowUp: (state, action) => {
      const updated = action.payload.data;
      state.model.etapas = state.model.etapas.map(
        (current) => ((current?.id === updated?.id)
          ? updated : current
        )
      );
      state.mode.sub = SUB_CONSULT_MODE;
    },
    removeEtapaModeloFollowUpById: (state, action) => {
      const { id } = action.payload.data;
      state.model.etapas = state.model.etapas.filter((current) => (
        current?.id !== id
      ));
      state.related.model.etapaModeloFollowUp = initialState.related.model.etapaModeloFollowUp;
    },
    resetRelatedEtapaModeloFollowUpModel: (state) => {
      state.related.model.etapaModeloFollowUp = {};

      if (!isSubCreateMode(state.mode.sub)) {
        state.mode.sub = NONE_SUB_MODE;
      }
    },
    addToList: (state, action) => {
      state.list = [...state.list, action.payload.data];
    },
    updateOnList: (state, action) => {
      state.list = state.list.map((current) => {
        if (current.id === action.payload.data?.id) {
          return action.payload.data;
        }
        return current;
      });
    },
    resetList: (state) => {
      state.list = [];
    },
    setResponse: (state, action) => {
      state.response = action.payload;
    },
    clearResponse: (state) => {
      state.response = {
        status: null,
        message: null,
        data: null,
      };
    },
    setError: (state, action) => {
      state.error = action.payload;
    },
    clearError: (state) => {
      state.error = initialState.error;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAllAsync.pending, (state) => {
        state.status = ALTERNATIVE_LOADING_STATUS;
      })
      .addCase(fetchAllAsync.fulfilled, (state, action) => {
        state.status = IDLE_STATUS;
        state.list = action.payload.data;
      })
      .addCase(fetchAllAsync.rejected, (state, action) => {
        state.status = FAILURE_STATUS;
        state.error = {
          ...action.error,
          message: i18n.t('com.muralis.qcx.erro.erroCarregarListaModelosFolloUpMensagem', { erro: action.error.message }),
        };
      })
      .addCase(fetchByIdAsync.pending, (state) => {
        state.status = LOADING_STATUS;
      })
      .addCase(fetchByIdAsync.fulfilled, (state, action) => {
        if (action.payload.response.status === 200) {
          const { analiseDesempenho } = action.payload.response.data;
          state.model = {
            ...action.payload.response.data,
            hasAnaliseDesempenho: !!analiseDesempenho,
          };
          state.status = SUCCESS_STATUS;
        }
      })
      .addCase(fetchByIdAsync.rejected, (state, action) => {
        state.status = FAILURE_STATUS;
        state.error = {
          ...action.error,
          message: i18n.t('com.muralis.qcx.erro.erroCarregarModeloFollowUpMensagem', { erro: action.error.message }),
        };
      })
      .addCase(activateByIdAsync.pending, (state) => {
        state.status = LOADING_STATUS;
      })
      .addCase(activateByIdAsync.fulfilled, (state, action) => {
        state.status = SUCCESS_STATUS;
        state.response.status = action.payload.response.status;
        state.response.data = action.payload.response.data;

        const { description } = action.payload.response.data;
        state.response.message = i18n.t('com.muralis.qcx.mensagem.modeloFollowUpAtivado', { modelo: description });

        if (state.response.status === 200) {
          state.list = state.list
            .map((current) => (
              current.id === action.payload.response.data.id
                ? action.payload.response.data
                : current
            ));
        }
      })
      .addCase(activateByIdAsync.rejected, (state, action) => {
        state.status = FAILURE_STATUS;
        state.error = {
          ...action.error,
          message: i18n.t('com.muralis.qcx.erro.erroAtivarModeloFollowUp', { erro: action.error.message }),
        };
      })
      .addCase(inactivateByIdAsync.pending, (state) => {
        state.status = LOADING_STATUS;
      })
      .addCase(inactivateByIdAsync.fulfilled, (state, action) => {
        state.status = SUCCESS_STATUS;
        state.response.status = action.payload.response.status;
        state.response.data = action.payload.response.data;

        const { description } = action.payload.response.data;
        state.response.message = i18n.t('com.muralis.qcx.mensagem.modeloFollowUpInativado', { modelo: description });

        if (state.response.status === 200) {
          state.list = state.list
            .map((current) => (
              current.id === action.payload.response.data.id
                ? action.payload.response.data
                : current
            ));
        }
      })
      .addCase(inactivateByIdAsync.rejected, (state, action) => {
        state.status = FAILURE_STATUS;
        state.error = {
          ...action.error,
          message: i18n.t('com.muralis.qcx.erro.erroInativarModeloFollowUp', { erro: action.error.message }),
        };
      });
  },
});

const {
  changeStatusTo,
  loading,
  success,
  failure,
  preparingAction,
  resetStatus,
  changeToCreateMode,
  changeToConsultMode,
  changeToUpdateMode,
  resetMode,
  changeToBackgroundCreateMode,
  resetBackgroundMode,
  changeToSubConsultMode,
  changeToSubUpdateMode,
  changeToSubCreateMode,
  resetSubMode,
  setModel,
  resetModel,
  setRelatedEtapaModeloFollowUpModel,
  addEtapaModeloFollowUp,
  updateEtapaModeloFollowUp,
  removeEtapaModeloFollowUpById,
  resetRelatedEtapaModeloFollowUpModel,
  addToList,
  updateOnList,
  resetList,
  setResponse,
  clearResponse,
  setError,
  clearError,
} = modeloFollowUpSlice.actions;

const selectStatus = (state) => state.modeloFollowUp.status;
const selectMode = (state) => state.modeloFollowUp.mode.main;
const selectBackgroundMode = (state) => state.modeloFollowUp.mode.background;
const selectSubMode = (state) => state.modeloFollowUp.mode.sub;
const selectModelosFollowUp = (state) => state.modeloFollowUp.list;
const selectModeloFollowUp = (state) => state.modeloFollowUp.model;
const selectRelatedEtapaModeloFollowUpModel = (state) => (
  state.modeloFollowUp.related.model.etapaModeloFollowUp
);
const selectError = (state) => state.modeloFollowUp.error;
const selectResponse = (state) => state.modeloFollowUp.response;

export {
  modeloFollowUpSlice,
  changeStatusTo,
  loading,
  success,
  failure,
  preparingAction,
  resetStatus,
  changeToCreateMode,
  changeToConsultMode,
  changeToUpdateMode,
  resetMode,
  changeToBackgroundCreateMode,
  resetBackgroundMode,
  changeToSubConsultMode,
  changeToSubUpdateMode,
  changeToSubCreateMode,
  resetSubMode,
  setModel,
  resetModel,
  setRelatedEtapaModeloFollowUpModel,
  addEtapaModeloFollowUp,
  updateEtapaModeloFollowUp,
  removeEtapaModeloFollowUpById,
  resetRelatedEtapaModeloFollowUpModel,
  addToList,
  updateOnList,
  resetList,
  setResponse,
  clearResponse,
  setError,
  clearError,
  fetchAllAsync,
  fetchByIdAsync,
  activateByIdAsync,
  inactivateByIdAsync,
  selectStatus,
  selectMode,
  selectBackgroundMode,
  selectSubMode,
  selectModeloFollowUp,
  selectModelosFollowUp,
  selectRelatedEtapaModeloFollowUpModel,
  selectError,
  selectResponse,
};

export default modeloFollowUpSlice.reducer;
